import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Loader2, Palette } from "lucide-react";

const inputCls = "bg-[#0b0811] border-white/10 text-white";
const FIELDS = [
  { key: "organisme", label: "Nom de l'organisme (bandeau haut)", placeholder: "Ex : EMS Electronics — par défaut : organisme de la certification" },
  { key: "ville", label: "Ville d'émission", placeholder: "Ex : Paris — par défaut : lieu de la session" },
  { key: "signataire_nom", label: "Signataire (nom)", placeholder: "Ex : Jean DUPONT" },
  { key: "signataire_titre", label: "Signataire (titre)", placeholder: "Ex : Directeur de l'organisme certificateur" },
];

export default function DiplomaConfigDialog({ api, open, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/certif/diploma-config").then(r => setCfg(r.data)).catch(() => setCfg(null));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/certif/diploma-config", cfg);
      setCfg(data);
      toast.success("Style du diplôme enregistré");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg" data-testid="diploma-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Palette className="w-4 h-4 text-[#f7c935]" /> Personnalisation du diplôme
          </DialogTitle>
        </DialogHeader>
        {!cfg ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#f7c935]" /></div>
        ) : (
          <div className="space-y-3">
            <p className="text-[11px] text-slate-500">Ces réglages s'appliquent à tous les diplômes PDF de votre organisme (couleurs, signataire, ville).</p>
            {FIELDS.map(f => (
              <div key={f.key}>
                <label className="text-[10px] uppercase text-slate-500">{f.label}</label>
                <Input className={inputCls} value={cfg[f.key] || ""} placeholder={f.placeholder}
                  onChange={e => setCfg({ ...cfg, [f.key]: e.target.value })}
                  data-testid={`diploma-cfg-${f.key}`} />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              {[["couleur_principale", "Couleur principale"], ["couleur_accent", "Couleur d'accent"]].map(([key, label]) => (
                <div key={key}>
                  <label className="text-[10px] uppercase text-slate-500">{label}</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={cfg[key] || "#000000"}
                      onChange={e => setCfg({ ...cfg, [key]: e.target.value })}
                      className="h-9 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                      data-testid={`diploma-cfg-${key}-picker`} />
                    <Input className={inputCls} value={cfg[key] || ""}
                      onChange={e => setCfg({ ...cfg, [key]: e.target.value })}
                      data-testid={`diploma-cfg-${key}`} />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="border-white/10 text-slate-300" onClick={onClose} data-testid="diploma-cfg-cancel-btn">Annuler</Button>
              <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] font-bold hover:bg-[#e6b825]" data-testid="diploma-cfg-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
